import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PaperPlaneTilt, Info, IdentificationCard, ChatCircleText, Clock, House, Car, WifiHigh, Ghost, Briefcase, Scales, LockKey } from '@phosphor-icons/react';
import { IntentToggle } from '../components/contact/IntentToggle';
import { OptionCard } from '../components/contact/OptionCard';
import { SuccessCard } from '../components/contact/SuccessCard';
import { ProgressBar } from '../components/contact/ProgressBar';
import { FormSection } from '../components/contact/FormSection';
import { ReadinessChecklist } from '../components/contact/ReadinessChecklist';
import { Button } from '../components/Button';
import { TextArea } from '../components/TextArea';
import { RadioRow, SliderField, CheckboxGroup, TriToggle, RatingScale } from '../components/contact/fields/CoreFields';
import { SelectField, SearchableSelect, StepperField, NumberInput, DateField, MultiSelectChips } from '../components/contact/fields/AdvancedFields';
import { COMPLIMENTS, COMPLAINTS, type Intent } from '../components/contact/options';
import * as Data from '../components/contact/formData';
export function ContactPage() {
  // Section 1: Sentiment
  const [intent, setIntent] = useState<Intent>('compliment');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Section 2: Identity
  const [shoeLoyalty, setShoeLoyalty] = useState<string>(Data.SHOE_LOYALTY[0]);
  const [pigeons, setPigeons] = useState(42);
  const [skeleton, setSkeleton] = useState<string>(Data.SKELETON_STATUS[0]);
  const [emperor, setEmperor] = useState<string>(Data.ROMAN_EMPERORS[0].value);
  const [beans, setBeans] = useState(1500);
  // Section 3: Communication
  const [ignoreMethods, setIgnoreMethods] = useState<string[]>([]);
  const [holdMusic, setHoldMusic] = useState<string>(Data.HOLD_MUSIC[0]);
  const [dinosaur, setDinosaur] = useState<string>(Data.DINOSAURS[0]);
  // Section 4: Temporal
  const [year, setYear] = useState<string>(Data.YEARS[0]);
  const [tuesdays, setTuesdays] = useState<number | ''>('');
  const [moonDate, setMoonDate] = useState('');
  const [heatDeath, setHeatDeath] = useState(50);
  // Section 5: Household
  const [gourds, setGourds] = useState(0);
  const [gooseCount, setGooseCount] = useState<number | ''>('');
  const [hauntedOttoman, setHauntedOttoman] = useState<'Yes' | 'No' | 'Unsure'>('Unsure');
  const [fridgeVibe, setFridgeVibe] = useState<string>(Data.REFRIGERATOR_VIBES[0]);
  // Section 6: Transportation
  const [horsepower, setHorsepower] = useState(1);
  const [turnSignal, setTurnSignal] = useState<string>(Data.TURN_SIGNALS[0]);
  const [vehicleDefeat, setVehicleDefeat] = useState<string>(Data.VEHICLES[0]);
  // Section 7: Internet
  const [captcha, setCaptcha] = useState<string>(Data.CAPTCHA_ALIGNMENT[0]);
  const [tabs, setTabs] = useState(42);
  const [cookies, setCookies] = useState<number | ''>('');
  const [usbPort, setUsbPort] = useState<string>(Data.USB_PORTS[0]);
  // Section 8: Existential
  const [raccoons, setRaccoons] = useState(0);
  const [realityMounted, setRealityMounted] = useState(100);
  const [monologue, setMonologue] = useState<string>(Data.INTERNAL_MONOLOGUE[0]);
  const [universes, setUniverses] = useState<number | ''>('');
  // Section 9: Professional
  const [meetingLength, setMeetingLength] = useState<string>(Data.MEETING_LENGTHS[0]);
  const [spiritAnimal, setSpiritAnimal] = useState<string>(Data.SPIRIT_ANIMALS[0]);
  const [buzzwords, setBuzzwords] = useState(0);
  // Section 10: Regulatory
  const [maritime, setMaritime] = useState<string>(Data.MARITIME_LAW[0]);
  const [exportClass, setExportClass] = useState<string>(Data.EXPORT_CLASSES[0]);
  const [curses, setCurses] = useState(0);
  const [tax, setTax] = useState<string>(Data.TAX_DEDUCTIBLE[0]);
  // Section 11: Final Gate (Required)
  const [planets, setPlanets] = useState<string[]>([]);
  const [moisture, setMoisture] = useState(70);
  const [doors, setDoors] = useState<number | null>(null);
  const [additionalQuestions, setAdditionalQuestions] = useState(1);
  // Checklist
  const [checklist, setChecklist] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const options = intent === 'compliment' ? COMPLIMENTS : COMPLAINTS;
  const selectedOption = options.find(o => o.id === selectedId) ?? null;
  const hasSentiment = selectedOption !== null;
  const hasPlanet = planets.length > 0;
  const hasDoors = doors !== null;
  const hasChecklist = checklist.length === Data.CHECKLIST_ITEMS.length;
  const canSubmit = hasSentiment && hasPlanet && hasDoors && hasChecklist;
  let missingHint = '';
  if (!hasSentiment) missingHint = 'Select an approved sentiment.';else if (!hasPlanet) missingHint = 'Awaiting planetary disclosure.';else if (!hasDoors) missingHint = 'Door confidence rating required.';else if (!hasChecklist) missingHint = 'Complete the readiness checklist.';
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitted(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  function handleReset() {
    setSubmitted(false);
    setSelectedId(null);
    setPlanets([]);
    setDoors(null);
    setChecklist([]);
  }
  return <div className="min-h-screen w-full bg-[hsl(var(--muted))]/30 pb-24" data-id="element-127">
      <ProgressBar data-id="element-128" />

      <main className="mx-auto mt-10 w-full max-w-2xl px-4" data-id="element-129">
        <header className="mb-10 border-2 border-[hsl(var(--foreground))] bg-[hsl(var(--card))] p-6 sm:p-8" data-id="element-130">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[hsl(var(--border))] pb-4 font-mono text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--muted-foreground))]" data-id="element-131">
            <span data-id="element-132">Form CU-1040 · Rev. 06/2026</span>
            <span data-id="element-133">OMB No. 0000-0000 · Approval Expired</span>
          </div>
          <h1 className="mt-5 font-serif text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))] sm:text-3xl" data-id="element-134">
            Request for Acknowledgment (Optional)
          </h1>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-[hsl(var(--muted-foreground))]" data-id="element-135">
            We don't accept custom messages — reading is hard, and your
            individual thoughts have already been pre-anticipated. Please
            complete the mandatory disclosures below.
          </p>
          <p className="mt-4 border-t border-[hsl(var(--border))] pt-3 text-[11px] italic text-[hsl(var(--muted-foreground))]" data-id="element-136">
            Paperwork Reduction Act Notice: This form increases paperwork.
            Estimated completion time: the rest of your natural life.
          </p>
        </header>

        <AnimatePresence mode="wait" data-id="element-137">
          {submitted && selectedOption ? <SuccessCard key="success" intent={intent} selectionLabel={selectedOption.label} onReset={handleReset} data-id="element-138" /> : <motion.form key="form" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2
        }} onSubmit={handleSubmit} className="border-2 border-[hsl(var(--foreground))] bg-[hsl(var(--card))] p-6 shadow-sm sm:p-8" data-id="element-139">
              {/* SECTION 1 */}
              <section className="mt-2" data-id="element-140">
                <header className="mb-7" data-id="element-141">
                  <div className="flex items-baseline justify-between gap-4 border-b-2 border-[hsl(var(--foreground))] pb-1.5" data-id="element-142">
                    <h2 className="font-mono text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--foreground))]" data-id="element-143">
                      Section 1 — Approved Sentiment
                    </h2>
                    <span className="shrink-0 font-mono text-[10px] text-[hsl(var(--muted-foreground))]" data-id="element-144">
                      CU-1040.1
                    </span>
                  </div>
                  <p className="mt-2 text-xs italic text-[hsl(var(--muted-foreground))]" data-id="element-145">
                    Select the pre-approved thought that most closely aligns
                    with your intent.
                  </p>
                </header>
                <div data-id="element-146">
                  <IntentToggle value={intent} onChange={next => {
                setIntent(next);
                setSelectedId(null);
              }} data-id="element-147" />
                  <div role="radiogroup" aria-label="Approved sentiments" className="mt-5 flex flex-col gap-2.5" data-id="element-148">
                    <AnimatePresence mode="wait" data-id="element-149">
                      <motion.div key={intent} initial={{
                    opacity: 0,
                    x: intent === 'compliment' ? -8 : 8
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.18
                  }} className="flex flex-col gap-2.5" data-id="element-150">
                        {options.map(option => <OptionCard key={option.id} option={option} selected={selectedId === option.id} onSelect={setSelectedId} data-id="element-151" />)}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </section>

              {/* SECTION 2 */}
              <FormSection index={2} icon={IdentificationCard} title="Identity Verification" caption="Confirming you are who you think you are." data-id="element-152">
                <RadioRow label="Primary Shoe Loyalty" options={Data.SHOE_LOYALTY} value={shoeLoyalty} onChange={setShoeLoyalty} data-id="element-153" />
                <SliderField label="Number of Pigeons Trusted" value={pigeons} onChange={setPigeons} max={500} formatValue={v => `${v} pigeons`} data-id="element-154" />
                <SelectField label="Current Skeleton Status" options={Data.SKELETON_STATUS} value={skeleton} onChange={setSkeleton} data-id="element-155" />
                <SearchableSelect label="Preferred Roman Emperor" options={Data.ROMAN_EMPERORS} value={emperor} onChange={setEmperor} data-id="element-156" />
                <StepperField label="Approximate Number of Beans Owned" value={beans} onChange={setBeans} step={100} data-id="element-157" />
              </FormSection>

              {/* SECTION 3 */}
              <FormSection index={3} icon={ChatCircleText} title="Communication Preferences" caption="How we will fail to communicate with you." data-id="element-158">
                <CheckboxGroup label="How should we ignore your message?" options={Data.IGNORE_METHODS} value={ignoreMethods} onChange={setIgnoreMethods} data-id="element-159" />
                <RadioRow label="Preferred Hold Music Genre" options={Data.HOLD_MUSIC} value={holdMusic} onChange={setHoldMusic} data-id="element-160" />
                <SelectField label="Emergency Contact's Favorite Dinosaur" options={Data.DINOSAURS} value={dinosaur} onChange={setDinosaur} data-id="element-161" />

                <div className="pt-4" data-id="element-162">
                  <TextArea label="Custom message" disabled rows={3} placeholder="Custom messages are not supported. They never were." hint="This field is permanently locked. Custom messages are not supported. They never were." data-id="element-163" />
                </div>
              </FormSection>

              {/* SECTION 4 */}
              <FormSection index={4} icon={Clock} title="Temporal Information" caption="Establishing your relationship with linear time." data-id="element-164">
                <SelectField label="Current Year According To You" options={Data.YEARS} value={year} onChange={setYear} data-id="element-165" />
                <NumberInput label="How Many Tuesdays Have You Experienced?" value={tuesdays} onChange={setTuesdays} data-id="element-166" />
                <DateField label="Last Time You Made Eye Contact With The Moon" value={moonDate} onChange={setMoonDate} data-id="element-167" />
                <SliderField label="Estimated Wait Time Before Heat Death of Universe" value={heatDeath} onChange={setHeatDeath} formatValue={v => `${v}B years`} data-id="element-168" />
              </FormSection>

              {/* SECTION 5 */}
              <FormSection index={5} icon={House} title="Household Information" caption="Failure to disclose gourds may delay your ignoral." data-id="element-169">
                <SliderField label="Number of Decorative Gourds" value={gourds} onChange={setGourds} max={1000} formatValue={v => `${v} gourds`} data-id="element-170" />
                <NumberInput label="Household Goose Count" value={gooseCount} onChange={setGooseCount} data-id="element-171" />
                <TriToggle label="Presence of Haunted Ottoman" value={hauntedOttoman} onChange={setHauntedOttoman} data-id="element-172" />
                <RadioRow label="Dominant Refrigerator Vibe" options={Data.REFRIGERATOR_VIBES} value={fridgeVibe} onChange={setFridgeVibe} data-id="element-173" />
              </FormSection>

              {/* SECTION 6 */}
              <FormSection index={6} icon={Car} title="Transportation" caption="Assessing your vehicular capabilities." data-id="element-174">
                <SliderField label="Number of Horsepower Personally Possessed" value={horsepower} onChange={setHorsepower} max={1000} formatValue={v => `${v} hp`} data-id="element-175" />
                <SelectField label="Preferred Turn Signal Rhythm" options={Data.TURN_SIGNALS} value={turnSignal} onChange={setTurnSignal} data-id="element-176" />
                <RadioRow label="Largest Vehicle You Believe You Could Defeat" options={Data.VEHICLES} value={vehicleDefeat} onChange={setVehicleDefeat} data-id="element-177" />
              </FormSection>

              {/* SECTION 7 */}
              <FormSection index={7} icon={WifiHigh} title="Internet Credentials" caption="Verifying your digital footprint." data-id="element-178">
                <RadioRow label="CAPTCHA Alignment" options={Data.CAPTCHA_ALIGNMENT} value={captcha} onChange={setCaptcha} data-id="element-179" />
                <SliderField label="Browser Tabs Currently Open" value={tabs} onChange={setTabs} max={10000} formatValue={v => `${v.toLocaleString()} tabs`} data-id="element-180" />
                <NumberInput label="Number of Times You've Clicked 'Accept Cookies'" value={cookies} onChange={setCookies} data-id="element-181" />
                <SelectField label="Most Trusted USB Port" options={Data.USB_PORTS} value={usbPort} onChange={setUsbPort} data-id="element-182" />
              </FormSection>

              {/* SECTION 8 */}
              <FormSection index={8} icon={Ghost} title="Existential Metrics" caption="Measuring your grip on reality." data-id="element-183">
                <SliderField label="Probability You Are Three Raccoons" value={raccoons} onChange={setRaccoons} formatValue={v => `${v}%`} data-id="element-184" />
                <SliderField label="Certainty Reality Is Properly Mounted" value={realityMounted} onChange={setRealityMounted} formatValue={v => `${v}%`} data-id="element-185" />
                <RadioRow label="How Loud Is Your Internal Monologue?" options={Data.INTERNAL_MONOLOGUE} value={monologue} onChange={setMonologue} data-id="element-186" />
                <NumberInput label="Number of Parallel Universes Currently Monitoring You" value={universes} onChange={setUniverses} data-id="element-187" />
              </FormSection>

              {/* SECTION 9 */}
              <FormSection index={9} icon={Briefcase} title="Professional Information" caption="Corporate synergy alignment." data-id="element-188">
                <RadioRow label="Preferred Meeting Length" options={Data.MEETING_LENGTHS} value={meetingLength} onChange={setMeetingLength} data-id="element-189" />
                <SelectField label="Corporate Spirit Animal" options={Data.SPIRIT_ANIMALS} value={spiritAnimal} onChange={setSpiritAnimal} data-id="element-190" />
                <SliderField label="Maximum Number of Buzzwords Safely Contained" value={buzzwords} onChange={setBuzzwords} max={500} formatValue={v => `${v} words`} data-id="element-191" />
              </FormSection>

              {/* SECTION 10 */}
              <FormSection index={10} icon={Scales} title="Regulatory Compliance" caption="Legal disclaimers and maritime law." data-id="element-192">
                <RadioRow label="Are You Currently Under Maritime Law?" options={Data.MARITIME_LAW} value={maritime} onChange={setMaritime} data-id="element-193" />
                <SelectField label="Export Classification of Your Thoughts" options={Data.EXPORT_CLASSES} value={exportClass} onChange={setExportClass} data-id="element-194" />
                <StepperField label="Number of Active Curses" value={curses} onChange={setCurses} max={100} data-id="element-195" />
                <RadioRow label="Is This Form Tax Deductible?" options={Data.TAX_DEDUCTIBLE} value={tax} onChange={setTax} data-id="element-196" />
              </FormSection>

              {/* SECTION 11 */}
              <FormSection index={11} icon={LockKey} title="Final Submission Gate" caption="These fields are mandatory for processing." data-id="element-197">
                <MultiSelectChips label="Select Every Planet You Have Visited" options={Data.PLANETS} value={planets} onChange={setPlanets} data-id="element-198" />
                <SliderField label="Confirm Your Approximate Moisture Content" value={moisture} onChange={setMoisture} formatValue={v => `${v}%`} data-id="element-199" />
                <RatingScale label="Rate Your Confidence In The Concept Of Doors" value={doors} onChange={setDoors} data-id="element-200" />
                <SliderField label="How Many Additional Questions Should This Form Have?" value={additionalQuestions} onChange={setAdditionalQuestions} min={1} max={10000} formatValue={v => `${v.toLocaleString()}`} data-id="element-201" />
              </FormSection>

              <ReadinessChecklist checkedItems={checklist} onChange={setChecklist} data-id="element-202" />

              <div className="mt-8" data-id="element-203">
                <Button type="submit" size="lg" disabled={!canSubmit} className="w-full" data-id="element-204">
                  <PaperPlaneTilt size={16} weight="fill" className="mr-2" data-id="element-205" />
                  Submit (it goes nowhere)
                </Button>
                {!canSubmit && <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]" data-id="element-206">
                    <Info size={13} data-id="element-207" />
                    {missingHint}
                  </p>}
              </div>
            </motion.form>}
        </AnimatePresence>

        <p className="mt-8 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--muted-foreground))]" data-id="element-208">
          Form CU-1040 · All submissions routed to the void · Response time:
          never
        </p>
      </main>
    </div>;
}